// Used by: Book, Payment, Transactions
import chevron from "../../assets/icons/chevron-down-grey.svg";
import downloadIcon from "../../assets/icons/download.svg";
import React, { useEffect, useState } from "react";
import SlideDown from "react-slidedown/lib/slidedown";
import getClassName from "../../utilities/getClassName";
import { NumericFormat } from "react-number-format";
import { isFloatingFleet } from "../../utilities/aircraft";
import { getTripTypeLabelFromLegCount, normalizeTripTypeDisplay } from "../../utilities/tripTypeDisplay";
import { formatLegDuration, getDate, getArrivalDayOffset } from "../../utilities/date";

/** Shortens a dd/MM/yyyy datetime string to dd/MM/yy e.g. "05/06/2026 10:00" → "05/06/26 10:00" */
const shortenDateYear = (value) => {
    if (!value) return value;
    return String(value).replace(/^(\d{2}\/\d{2}\/)(\d{2})(\d{2})/, "$1$3");
};

const OrderSummary = ({ template, productInfo, invoiceUrl, showExtraContent, classNames, isOpen, isOpenOnLoad }) => {
    // State
    const [summaryOpen, setSummaryOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSummaryOpen(true);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpenOnLoad) {
            setSummaryOpen(true);
        }
    }, [isOpenOnLoad]);

    let className = ["order-summary"];

    if (classNames) {
        if (typeof classNames === "object") {
            className = className.concat(classNames);
        }
    }

    const tripTypeSummaryLabel =
        template === "flight-booking" && productInfo
            ? normalizeTripTypeDisplay(productInfo.tripType) ||
              getTripTypeLabelFromLegCount(productInfo.flights?.length)
            : null;

    const useSimpleFlightDetailsPanel = Array.isArray(classNames) && classNames.includes("order-summary--simple");

    const flightDetailsContent =
        template === "flight-booking" && productInfo ? (
            <div className="order-summary__details-content">
                {productInfo.flights.map((flight, i) => {
                    const getLegTitle = () => {
                        if (productInfo.flights.length === 1) {
                            return null;
                        }
                        if (productInfo.flights.length === 2) {
                            return i === 0 ? "Outbound" : "Inbound";
                        }
                        return `Leg ${i + 1} - Departure & Arrival`;
                    };

                    const legTitle = getLegTitle();
                    const arrivalDayOffset = getArrivalDayOffset(
                        getDate(flight.departureTime) || flight.departureDate,
                        getDate(flight.arrivalTime) || flight.arrivalDate
                    );
                    const tripDuration = Array.isArray(productInfo.duration)
                        ? productInfo.duration[i]
                        : productInfo.duration;
                    const legDuration = formatLegDuration(flight.duration || flight.flightTime || tripDuration);

                    return (
                        <React.Fragment key={i}>
                            {legTitle != null && <p className="order-summary__item-group-title">{legTitle}</p>}
                            <div className="order-summary__leg">
                                <div className="order-summary__item">
                                    <div className="order-summary__item__gold">Departure</div>
                                    <div className="order-summary__item__bold">
                                        {flight.flightFromIcao} {flight.flightFromLong}{" "}
                                        {flight.flightFromCountryCode && <>, {flight.flightFromCountryCode}</>}
                                    </div>
                                    <div>
                                        {shortenDateYear(flight.departureTime)} {flight.departureTimeZone}
                                    </div>
                                    <div>Duration: {legDuration || "-"}</div>
                                </div>
                                <div className="order-summary__item">
                                    <div className="order-summary__item__gold">Arrival</div>
                                    <div className="order-summary__item__bold">
                                        {flight.flightToIcao} {flight.flightToLong}{" "}
                                        {flight.flightToCountryCode && <>, {flight.flightToCountryCode}</>}
                                    </div>
                                    <div>
                                        {shortenDateYear(flight.arrivalTime)}
                                        {arrivalDayOffset > 0 && (
                                            <sup
                                                className="arrival-time__day-offset"
                                                aria-label={`Arrives ${arrivalDayOffset} day${arrivalDayOffset > 1 ? "s" : ""} later`}
                                            >
                                                +{arrivalDayOffset}
                                            </sup>
                                        )}{" "}
                                        {flight.arrivalTimeZone}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
                <div className="order-summary__item">
                    <div className="order-summary__item__gold">Details</div>

                    {tripTypeSummaryLabel && (
                        <div className="order-summary__item__detail">
                            <div className="order-summary__item__detail__title">Trip type:</div>
                            <div className="order-summary__item__detail__quantity">{tripTypeSummaryLabel}</div>
                        </div>
                    )}

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Aircraft:</div>
                        <div className="order-summary__item__detail__quantity">
                            {productInfo.aircraftType}
                            {productInfo.category ? ` (${productInfo.category})` : ""}
                            {isFloatingFleet(productInfo) ? " (Floating Fleet)" : ""}
                        </div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Manufactured:</div>
                        <div className="order-summary__item__detail__quantity">{productInfo.yom || "-"}</div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Refurbished:</div>
                        <div className="order-summary__item__detail__quantity">
                            {productInfo.refurbishedYear && productInfo.refurbishedYear !== ""
                                ? productInfo.refurbishedYear
                                : "-"}
                        </div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Pax:</div>
                        <div className="order-summary__item__detail__quantity">{productInfo.passengers}</div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Luggage:</div>
                        <div className="order-summary__item__detail__quantity">
                            {productInfo.luggage} Bag{productInfo.luggage === 1 ? "" : "s"} (Max 20kg Each)
                        </div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Catering:</div>
                        <div className="order-summary__item__detail__quantity">{productInfo.cateringOption || "-"}</div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">Pets:</div>
                        <div className="order-summary__item__detail__quantity">
                            {productInfo.pets != null && productInfo.pets !== "" ? String(productInfo.pets) : "-"}
                        </div>
                    </div>

                    <div className="order-summary__item__detail">
                        <div className="order-summary__item__detail__title">CO₂:</div>
                        <div className="order-summary__item__detail__quantity">
                            {
                                <NumericFormat
                                    displayType="text"
                                    value={productInfo.carbonEmissions}
                                    thousandSeparator
                                    decimalScale={2}
                                />
                            }
                            T
                        </div>
                    </div>
                </div>
            </div>
        ) : null;

    return (
        <aside className={getClassName(className)}>
            {/* Flight booking template */}
            {template === "flight-booking" && (
                <>
                    <button
                        onClick={() => {
                            if (!isOpen) {
                                setSummaryOpen(!summaryOpen);
                            }
                        }}
                        className="order-summary__title"
                    >
                        Flight Details
                        <img
                            className={getClassName([
                                "order-summary__title__chevron",
                                summaryOpen && "order-summary__title__chevron--rotated",
                            ])}
                            src={chevron}
                            alt=""
                        />
                    </button>
                    <div className="order-summary__line" />

                    {useSimpleFlightDetailsPanel ? (
                        summaryOpen ? (
                            flightDetailsContent
                        ) : null
                    ) : (
                        <SlideDown
                            closed={!summaryOpen}
                            transitionOnAppear={false}
                            className="order-summary__slidedown"
                        >
                            {flightDetailsContent}
                        </SlideDown>
                    )}
                </>
            )}

            {showExtraContent && (
                <>
                    {invoiceUrl && (
                        <div className="order-summary__button-wrapper">
                            <a href={invoiceUrl} target="_blank" rel="noreferrer" className="button button--secondary">
                                <img src={downloadIcon} alt="" />
                                Download invoice
                            </a>
                        </div>
                    )}

                    <div className="order-summary__whatsapp">
                        Any questions or difficulties please contact your broker via WhatsApp.
                    </div>
                </>
            )}
        </aside>
    );
};

export default OrderSummary;
